import React, { forwardRef } from 'react'
import {
  Button,
  ButtonProps,
  CircularProgress,
  CircularProgressProps,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import TinyColor from 'tinycolor2'

import Colors from '../../constants/colors'
import { ClassesType } from '../../types'

const getColorWithAlpha = (color: string, alpha: number) =>
  TinyColor(color).setAlpha(alpha).toRgbString()

const useStyles = makeStyles(
  theme => ({
    button: {
      position: 'relative',
      height: 40,
      width: (props: UseStylesProps) =>
        props.fullWidth ? undefined : 'fit-content',
      minWidth: 80,
      borderRadius: 23.5,
      textTransform: 'unset',
      fontWeight: 'bold',
      boxShadow: 'none',
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      whiteSpace: 'nowrap',
      transition:
        'box-shadow 0.2s cubic-bezier(0.4, 0, 1, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      '&$disabled': {
        color: `${getColorWithAlpha(Colors.DARK_TEXT, 0.26)}`,
        backgroundColor: `${getColorWithAlpha(Colors.DARK_TEXT, 0.12)}`,
      },
    },
    primary: {
      backgroundColor: Colors.PRIMARY,
      color: Colors.LIGHT_TEXT,
      '&&&&:hover': {
        boxShadow: `0 14px 26px -12px ${getColorWithAlpha(
          Colors.PRIMARY,
          0.42,
        )},
      0 4px 23px 0px ${getColorWithAlpha(Colors.DARK_TEXT, 0.12)},
      0 8px 10px -5px ${getColorWithAlpha(Colors.PRIMARY, 0.2)}`,
        backgroundColor: TinyColor(Colors.PRIMARY).lighten(5).toHexString(),
      },
      '&&&&:active': {
        boxShadow: 'none',
        backgroundColor: TinyColor(Colors.PRIMARY).darken(15).toHexString(),
      },
    },
    inverted: {
      backgroundColor: Colors.LIGHT_TEXT,
      color: Colors.PRIMARY,
      border: `1px solid ${Colors.PRIMARY}`,
      '&&&&:hover': {
        boxShadow: `0 14px 26px -12px ${getColorWithAlpha(
          Colors.PRIMARY,
          0.42,
        )},
      0 4px 23px 0px ${getColorWithAlpha(Colors.DARK_TEXT, 0.12)},
      0 8px 10px -5px ${getColorWithAlpha(Colors.PRIMARY, 0.2)}`,
        backgroundColor: Colors.GREY100,
      },
      '&&&&:active': {
        boxShadow: 'none',
        backgroundColor: Colors.GREY100,
      },
    },
    secondary: {
      backgroundColor: Colors.LIGHT_TEXT,
      color: Colors.GREY500,
      border: `1px solid ${Colors.GREY300}`,
      '&&&&:hover': {
        boxShadow: `0 14px 26px -12px ${getColorWithAlpha(
          Colors.DARK_TEXT,
          0.42,
        )},
      0 4px 23px 0px ${getColorWithAlpha(Colors.DARK_TEXT, 0.12)},
      0 8px 10px -5px ${getColorWithAlpha(Colors.DARK_TEXT, 0.2)}`,
        backgroundColor: Colors.GREY100,
      },
      '&&&&:active': {
        boxShadow: 'none',
        backgroundColor: Colors.GREY100,
      },
    },
    important: {
      backgroundColor: Colors.IMPORTANT,
      color: Colors.LIGHT_TEXT,
      '&&&&:hover': {
        boxShadow: `0 14px 26px -12px ${getColorWithAlpha(
          Colors.IMPORTANT,
          0.42,
        )},
      0 4px 23px 0px ${getColorWithAlpha(Colors.DARK_TEXT, 0.12)},
      0 8px 10px -5px ${getColorWithAlpha(Colors.IMPORTANT, 0.2)}`,
        backgroundColor: TinyColor(Colors.IMPORTANT).lighten(5).toHexString(),
      },
      '&&&&:active': {
        boxShadow: `box-shadow: 0 4px 23px 0 ${getColorWithAlpha(
          Colors.DARK_TEXT,
          0.12,
        )}`,
        backgroundColor: TinyColor(Colors.IMPORTANT).darken(5).toHexString(),
      },
    },
    legacy: {
      color: Colors.LIGHT_TEXT,
      backgroundColor: Colors.SECONDARY,
      '&&&&:hover': {
        boxShadow: `0 14px 26px -12px ${getColorWithAlpha(
          Colors.SECONDARY,
          0.42,
        )},
      0 4px 23px 0px ${getColorWithAlpha(Colors.DARK_TEXT, 0.12)},
      0 8px 10px -5px ${getColorWithAlpha(Colors.SECONDARY, 0.2)}`,
        backgroundColor: TinyColor(Colors.SECONDARY).lighten(5).toHexString(),
      },
      '&&&&:active': {
        boxShadow: 'none',
        backgroundColor: TinyColor(Colors.SECONDARY).darken(5).toHexString(),
      },
    },
    disabled: {},
    spinner: {
      position: 'absolute',
      left: (props: UseStylesProps) => `calc(50% - ${props.spinnerSize / 2}px)`,
    },
  }),
  { name: 'ButtonWithLoader' },
)

export interface ButtonWithLoaderProps
  extends Omit<ButtonProps, 'color' | 'classes'> {
  readonly classes?: ClassesType<typeof useStyles>
  readonly color?: 'primary' | 'secondary' | 'important' | 'legacy' | 'inverted'
  readonly loading?: boolean
  readonly spinnerProps?: CircularProgressProps
}

interface UseStylesProps {
  classes?: ClassesType<typeof useStyles>
  fullWidth?: boolean
  spinnerSize: number
}

const ButtonWithLoader = forwardRef<HTMLButtonElement, ButtonWithLoaderProps>(
  function ButtonWithLoader(
    {
      children,
      color = 'primary',
      disabled = false,
      onClick,
      loading = false,
      fullWidth,
      spinnerProps: {
        color: spinnerColor = 'primary',
        size = 24,
        className: spinnerClassName,
        ...restSpinnerProps
      } = {},
      className: classNameProp,
      classes: classesProp,
      ...rest
    },
    ref,
  ) {
    const useStylesProps: UseStylesProps = {
      classes: classesProp,
      spinnerSize: size as number,
      fullWidth,
    }
    const classes = useStyles(useStylesProps)
    const isDisabled = disabled || loading

    return (
      <Button
        disableRipple
        className={classNames(classNameProp, classes.button, {
          [classes.disabled]: isDisabled,
          [classes.primary]: color === 'primary',
          [classes.secondary]: color === 'secondary',
          [classes.important]: color === 'important',
          [classes.legacy]: color === 'legacy',
          [classes.inverted]: color === 'inverted',
        })}
        disabled={isDisabled}
        fullWidth={fullWidth}
        ref={ref}
        onClick={onClick}
        {...rest}
      >
        {children}
        {loading && (
          <CircularProgress
            className={classNames(classes.spinner, spinnerClassName)}
            color={spinnerColor}
            size={size}
            {...restSpinnerProps}
          />
        )}
      </Button>
    )
  },
)

export default ButtonWithLoader
